/**
 * Secret Santa Service
 * Functions to interact with Secret Santa Cloud Functions
 */

import { httpsCallable } from 'firebase/functions'
import { functions } from '@/config/firebase'

export interface SecretSantaFormData {
  title: string
  description: string
  eventDate: Date | null
  budget: number | null
  currency: string
  allowSelfDraw: boolean
}

export interface ParticipantFormData {
  name: string
  email: string
  wishlistId?: string
}

/**
 * Create a new Secret Santa event
 */
export async function createSecretSanta(data: SecretSantaFormData) {
  const createSecretSantaFn = httpsCallable(functions, 'createSecretSanta')
  const result = await createSecretSantaFn({
    ...data,
    eventDate: data.eventDate ? data.eventDate.toISOString() : null,
  })
  return result.data as { id: string; accessToken: string; shareLink: string; message: string }
}

/**
 * Get Secret Santa event by access token
 */
export async function getSecretSantaByToken(accessToken: string) {
  const getSecretSantaByTokenFn = httpsCallable(functions, 'getSecretSantaByToken')
  const result = await getSecretSantaByTokenFn({ accessToken })
  const data = result.data as { event: any }
  return data.event
}

/**
 * Add participant to Secret Santa event
 */
export async function addParticipant(santaId: string, data: ParticipantFormData) {
  const addParticipantFn = httpsCallable(functions, 'addParticipant')
  const result = await addParticipantFn({ santaId, ...data })
  return result.data as { message: string }
}

/**
 * Remove participant from Secret Santa event
 */
export async function removeParticipant(santaId: string, participantUserId: string) {
  const removeParticipantFn = httpsCallable(functions, 'removeParticipant')
  const result = await removeParticipantFn({ santaId, participantUserId })
  return result.data as { message: string }
}

/**
 * Set exclusions for current user
 */
export async function setExclusions(santaId: string, excludedUserIds: string[]) {
  const setExclusionsFn = httpsCallable(functions, 'setExclusions')
  const result = await setExclusionsFn({ santaId, excludedUserIds })
  return result.data as { message: string }
}

/**
 * Draw names for Secret Santa
 */
export async function drawNames(santaId: string) {
  const drawNamesFn = httpsCallable(functions, 'drawNames')
  const result = await drawNamesFn({ santaId })
  return result.data as { message: string; assignmentsCount: number }
}

/**
 * Get my assignment (who I'm giving to)
 */
export async function getMyAssignment(santaId: string) {
  const getMyAssignmentFn = httpsCallable(functions, 'getMyAssignment')
  const result = await getMyAssignmentFn({ santaId })
  return result.data as {
    receiverName: string
    receiverWishlistId: string | null
    eventTitle: string
    eventDate: any
    budget: number | null
    currency: string
  }
}

/**
 * Link wishlist to participant
 */
export async function linkWishlistToParticipant(santaId: string, wishlistId: string) {
  const linkWishlistFn = httpsCallable(functions, 'linkWishlistToParticipant')
  const result = await linkWishlistFn({ santaId, wishlistId })
  return result.data as { message: string }
}
